import { EditorState, ContentState, SelectionState, ContentBlock, Modifier, EntityInstance, DraftHandleValue } from 'draft-js';


/**
 * Strategy to find autocompleted text using entities.
 * Used by Draft.js decorator to identify and style autocompleted text.
 *
 * @param contentBlock - The current block of content being searched
 * @param callback - Function to call when an entity range is found
 * @param contentState - The current content state of the editor
 */
export const findAutocompleteText = (
  contentBlock: ContentBlock,
  callback: (start: number, end: number) => void,
  contentState: ContentState
) => {
  contentBlock.findEntityRanges(
    (character) => {
      const entityKey: string = character.getEntity();
      if (entityKey === null) {
        return false;
      }
      return contentState.getEntity(entityKey).getType() === 'AUTOCOMPLETE';
    },
    callback
  );
};


/**
 * Check if the cursor is inside an autocomplete entity.
 * Used to prevent editing of autocompleted text.
 *
 * @param cursorState - Current editor state containing cursor position
 * @returns boolean - True if cursor is inside autocomplete entity
 */
export const isCursorInsideAutocomplete = (cursorState: EditorState): boolean => {
  const selection: SelectionState = cursorState.getSelection();
  const content: ContentState = cursorState.getCurrentContent();
  const block: ContentBlock = content.getBlockForKey(selection.getStartKey());
  const entityKey: string | undefined = block.getEntityAt(selection.getStartOffset());


  if (entityKey) {
    const entity: EntityInstance = content.getEntity(entityKey);
    let start: number = selection.getStartOffset();
    // Find start of entity
    while (start > 0 && block.getEntityAt(start - 1) === entityKey) {
      start--;
    }
    // Allow deletion only from start of entity
    return !(entity.getType() === 'AUTOCOMPLETE' &&
             selection.getStartOffset() === start &&
             selection.getStartOffset() === selection.getEndOffset());
  }
  return false;
};


/**
 * Inserts a suggestion at the position of the last '<>' trigger before the cursor.
 * Creates an immutable entity for the inserted text.
 *
 * @param editorState - Current state of the editor
 * @param suggestion - Text to insert as autocomplete suggestion
 * @returns EditorState - New editor state with inserted suggestion
 */
export const insertAutocompleteSuggestion = (
  editorState: EditorState,
  suggestion: string
): EditorState => {
  const contentState: ContentState = editorState.getCurrentContent();
  const selection: SelectionState = editorState.getSelection();
  const blockKey: string = selection.getStartKey();
  const block: ContentBlock = contentState.getBlockForKey(blockKey);
  const text: string = block.getText();
  const cursorPosition: number = selection.getStartOffset();
 
  // Find the last '<>' before or at the cursor position
  const textBeforeCursor: string = text.slice(0, cursorPosition);
  const lastAngleBracketIndex: number = textBeforeCursor.lastIndexOf('<>');
 
  // Only proceed if we found '<>' before the cursor
  if (lastAngleBracketIndex !== -1) {
    // Create a selection from the found '<>' to the current cursor position
    const newSelection: SelectionState = selection.merge({
      anchorOffset: lastAngleBracketIndex,
      focusOffset: selection.getFocusOffset(),
    });


    // Create an entity for the autocompleted text
    const contentStateWithEntity: ContentState = contentState.createEntity(
      'AUTOCOMPLETE',
      'IMMUTABLE',
      { suggestion }
    );
    const entityKey: string = contentStateWithEntity.getLastCreatedEntityKey();


    // Insert the suggestion and apply the entity
    let newContentState: ContentState = Modifier.replaceText(
      contentStateWithEntity,
      newSelection,
      suggestion,
      undefined,
      entityKey
    );


    // Add space for better UX
    const spaceSelection: SelectionState = newContentState.getSelectionAfter();
    newContentState = Modifier.insertText(newContentState, spaceSelection, ' ');


    // Create and return the new editor state
    return EditorState.push(
      editorState,
      newContentState,
      'insert-characters'
    );
  }
 
  // If no trigger was found, return the original state
  return editorState;
};


/**
 * Prevents character insertion inside autocomplete entities.
 * Part of Draft.js's handling of user input.
 */
export const handleBeforeInput = (chars: string, editorState: EditorState): DraftHandleValue => {
  if (isCursorInsideAutocomplete(editorState)) {
    return "handled";
  }
  return "not-handled";
};


/**
 * Prevents pasting text inside autocomplete entities.
 * Part of Draft.js's handling of paste events.
 */
export const handlePastedText = (text: string, html: string | undefined, editorState: EditorState): DraftHandleValue => {
  if (isCursorInsideAutocomplete(editorState)) {
    return "handled";
  }
  return "not-handled";
};


/**
 * Prevents newlines inside autocomplete entities.
 * Part of Draft.js's handling of return/enter key.
 */
export const handleReturn = (e: React.KeyboardEvent, editorState: EditorState): DraftHandleValue => {
  if (isCursorInsideAutocomplete(editorState)) {
    e.preventDefault();
    return "handled";
  }
  return "not-handled";
};